import React, { useContext, useState } from "react";
import { Loader2 } from "lucide-react";
import axios from "../../utils/axios";
import { AuthContext } from "../../contexts/Auth/Auth.Context";
import { REACT_APP_SESSION_DATA } from "../../constants";
import { Link } from "react-router-dom";

interface FormData {
  fullName: string;
  email:    string;
  password: string;
}

const fieldCls =
  'w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500 transition-all';

export function RegistrationForm(_props: React.ComponentPropsWithoutRef<"div">) {
  const { dispatch } = useContext(AuthContext);

  const [loading, setLoading]           = useState(false);
  const [formData, setFormData]         = useState<FormData>({ fullName: '', email: '', password: '' });
  const [errorMsg, setErrorMsg]         = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMsg('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    axios
      .post('/users/sign-up', { name: formData.fullName, password: formData.password, username: formData.email })
      .then((res) => {
        if (res.status === 200) {
          localStorage.setItem(REACT_APP_SESSION_DATA, res.data?.token);
          dispatch({ type: 'SIGN_UP', token: res.data?.token, data: res.data?.user });
        }
      })
      .catch((err: any) => { setErrorMsg(err?.message ?? 'Registration failed. Please try again.'); })
      .finally(() => setLoading(false));
  };

  return (
    <div className="min-h-[calc(100vh-120px)] flex" style={{ background: '#fafaf7' }}>

      {/* ── Left: brand panel ── */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-12 xl:p-16 relative overflow-hidden"
        style={{ background: '#052e16' }}
      >
        <img
          src="/images/elephant.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.2 }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#052e16]/80 via-[#052e16]/60 to-transparent pointer-events-none" />

        {/* Logo */}
        <div className="relative z-10">
          <a href="/" className="flex items-center gap-3">
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>
              WITS
            </span>
            <span className="text-green-400 text-xs tracking-[0.15em] uppercase font-medium mt-1" style={{ fontFamily: 'var(--font-body)' }}>
              by LAPA Africa
            </span>
          </a>
        </div>

        {/* Copy */}
        <div className="relative z-10">
          <h2
            className="text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-5"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Join the network<br />
            <span className="text-green-400">protecting Africa</span>
          </h2>
          <p
            className="text-white/55 text-sm leading-relaxed max-w-sm mb-8"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Create your account and become part of a growing community of rangers,
            researchers, and advocates driving evidence-based conservation.
          </p>

          {/* Feature bullets */}
          {[
            'Access live incident data and case records',
            'Submit and track field reports securely',
            'Collaborate with conservation partners',
          ].map((item) => (
            <div key={item} className="flex items-center gap-3 mb-3">
              <svg fill="none" viewBox="0 0 24 24" stroke="#4ade80" strokeWidth={2.5} className="shrink-0 w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-white/65 text-sm" style={{ fontFamily: 'var(--font-body)' }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right: form ── */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-12 xl:px-16">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <Link to="/" className="flex items-center gap-2 mb-10 lg:hidden">
            <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-display)' }}>WITS</span>
            <span className="text-green-700 text-xs tracking-[0.12em] uppercase font-medium" style={{ fontFamily: 'var(--font-body)' }}>by LAPA Africa</span>
          </Link>

          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
            Create account
          </h1>
          <p className="text-gray-500 text-sm mb-8" style={{ fontFamily: 'var(--font-body)' }}>
            Already have an account?{' '}
            <Link to="/accounts/login" className="text-green-700 font-semibold hover:underline">
              Sign in
            </Link>
          </p>

          {errorMsg && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" style={{ fontFamily: 'var(--font-body)' }}>
              {errorMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <label htmlFor="fullName" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>
                Full Name
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Your full name"
                required
                className={fieldCls}
              />
            </div>

            <div>
              <label htmlFor="regEmail" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>
                Email Address
              </label>
              <input
                id="regEmail"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                className={fieldCls}
              />
            </div>

            <div>
              <label htmlFor="regPassword" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>
                Password
              </label>
              <div className="relative">
                <input
                  id="regPassword"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  required
                  className={`${fieldCls} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-400 -mt-1" style={{ fontFamily: 'var(--font-body)' }}>
              By creating an account you agree to our{' '}
              <Link to="/faqs" className="text-green-700 hover:underline">terms of use</Link>{' '}
              and{' '}
              <Link to="/faqs" className="text-green-700 hover:underline">privacy policy</Link>.
            </p>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-full bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              {loading && <Loader2 className="animate-spin w-4 h-4" />}
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
